import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Redirect, Route, RouteProps, Switch } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Layout } from 'antd';
import { injectStores } from '@mobx-devtools/tools';
import UserStore from './stores/UserStore';
import { ThreeDViewer } from './components/Display/ThreeDViewer';
import { LandingPage, PrivateRouter, PublicRouter, SuccessPage } from './layouts';
import PortalLoading from './components/PortalLoading';
import PortalHeader from 'layouts/sections/PortalHeader';
import MobileNav from 'layouts/sections/MobileNav';
import { ShowEmail } from './pages/BuildPart/Email/ShowEmail';
import RedirectPreservingUTMQuery from './common/RedirectPreservingUTMQuery';

import './styles/tailwind.css';
import './assets/css/light-theme.css';
import LuckyOrangeIntegration from './components/LuckyOrangeIntegration';
import SentryNavigationTracker from './components/SentryNavigationTracker';
import useCaptureUTM from './hooks/useCaptureUTM';
import UIStore from '@/stores/UIStore';
import { setAuthScope } from '@partsbadger/utils';

if (process.env.NODE_ENV !== 'production') {
    // MobX devtools
    injectStores({ UserStore });
}

const PrivateRoute = observer(({ component: ChildComponent, ...restProps }: RouteProps) => {
    return (
        <Route
            {...restProps}
            render={props => {
                if (!UserStore.isAuthenticated) {
                    const next = window.location.pathname;
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.set('next', next);
                    return <Redirect to={`/login?${searchParams.toString()}`} />;
                } else {
                    const RenderComponent = ChildComponent as React.ComponentType<typeof props>;
                    return <RenderComponent {...props} />;
                }
            }}
        />
    );
});

export const App = observer(() => {
    const [loading, setLoading] = useState(true);

    useCaptureUTM();

    useEffect(() => {
        UIStore.selectBreakPoint('lg');

        const update = () => {
            UIStore.updateWindowDimensions(window.innerWidth, window.innerHeight);
        };

        // set inicial
        update();

        // listener
        window.addEventListener('resize', update);

        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        setLoading(true);
        setAuthScope('customer');

        const loadSession = async () => {
            try {
                if (UserStore.token) {
                    await UserStore.pullUser();
                } else {
                    await UserStore.restoreSession();
                }
            } catch (error) {
                console.error('Error loading user session:', error);
            } finally {
                setLoading(false);
            }
        };

        loadSession();
    }, []);

    return loading ? (
        <PortalLoading />
    ) : (
        <Router>
            <SentryNavigationTracker />
            <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <LuckyOrangeIntegration />
                <>
                    <Switch>
                        <Route path="/viewer/:quote_uuid/:item_uuid" component={ThreeDViewer} />
                        <PrivateRoute path="/my-account" component={PrivateRouter} />
                        <Route exact path="/your-online-machine-shop" component={LandingPage} />
                        <Route exact path="/success" component={SuccessPage} />
                        <Route path="/quick-turn" render={() => <RedirectPreservingUTMQuery to="/" />} />
                        <Route path="/show/email/:notification_id" component={ShowEmail} />
                        <Route path="/" component={PublicRouter} />
                    </Switch>
                </>
                <MobileNav />
            </div>
        </Router>
    );
});
